import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { DevBoxAddOnActionState } from '../../../constants/add-ons';
import {
    enableDevBoxAddOn,
    EnableDevBoxAddOnResponse,
    GetDevBoxAddOnResponse,
} from '../../../data/services/data-plane-api/add-ons';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import { createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault } from '../../../utilities/failure';
import {
    enableDevBoxAddOnAccepted,
    enableDevBoxAddOn as enableDevBoxAddOnActionCreator,
    enableDevBoxAddOnError,
    enableDevBoxAddOnFailed,
    enableDevBoxAddOnSuccess,
    enableDevBoxAddOnWithPolling,
    enableDevBoxAddOnWithPollingError,
    enableDevBoxAddOnWithPollingFailed,
    enableDevBoxAddOnWithPollingSuccess,
    pollNonTerminalDevBoxAddOn,
} from '../../actions/add-ons/add-ons-action-creators';
import {
    EnableDevBoxAddOnAcceptedAction,
    EnableDevBoxAddOnAction,
    EnableDevBoxAddOnWithPollingAction,
} from '../../actions/add-ons/add-ons-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';

export function* enableDevBoxAddOnSaga(action: EnableDevBoxAddOnAction): SagaIterator {
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, name } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );
        const response: EnableDevBoxAddOnResponse = yield call(enableDevBoxAddOn, id, name, accessToken, activityId);
        if (isFailureResponse(response)) {
            yield put(enableDevBoxAddOnFailed({ failure: response, id }, meta));
            yield resolveAction(action, response);
            return;
        }
        yield put(enableDevBoxAddOnSuccess({ id }, meta));
        yield resolveAction(action, response);
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.EnableDevBoxAddOn);
        yield put(enableDevBoxAddOnError({ error, id }, meta));
        yield rejectAction(action, error);
    }
}

/**
 * Enable DevBox AddOn with polling - Complete flow that enables and waits for completion
 */
export function* enableDevBoxAddOnWithPollingSaga(action: EnableDevBoxAddOnWithPollingAction): SagaIterator {
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, name } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const response: EnableDevBoxAddOnResponse = yield call(enableDevBoxAddOn, id, name, accessToken, activityId);
        if (isFailureResponse(response)) {
            yield put(enableDevBoxAddOnWithPollingFailed({ failure: response, id }, meta));
            yield resolveAction(action, response);
            return;
        }

        yield put(enableDevBoxAddOnAccepted({ id, name, result: response.data }, meta));
        yield resolveAction(action, response);
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.EnableDevBoxAddOn);
        yield put(enableDevBoxAddOnWithPollingError({ error, id }, meta));
        yield rejectAction(action, error);
    }
}

export function* enableDevBoxAddOnWithPollingAcceptedSaga(action: EnableDevBoxAddOnAcceptedAction): SagaIterator {
    const { meta, payload } = action;
    const { id, name } = payload;

    try {
        const response: GetDevBoxAddOnResponse = yield putAndAwait(pollNonTerminalDevBoxAddOn({ id, name }));
        if (isFailureResponse(response)) {
            yield put(enableDevBoxAddOnWithPollingFailed({ failure: response, id }, meta));
            return;
        }
        const { data } = response;
        const { actionState } = data;

        switch (actionState) {
            case DevBoxAddOnActionState.Enabled:
                yield put(enableDevBoxAddOnWithPollingSuccess({ id }, meta));
                break;
            default:
                // No error body in response, so just pass undefined to function
                const failure = createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
                    undefined,
                    FailureOperation.EnableDevBoxAddOn
                );
                yield put(enableDevBoxAddOnWithPollingFailed({ failure, id }, meta));
                break;
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.EnableDevBoxAddOn);
        yield put(enableDevBoxAddOnWithPollingError({ error, id }, meta));
    }
}

export function* enableDevBoxAddOnListenerSaga(): SagaIterator {
    yield takeEvery(enableDevBoxAddOnActionCreator, enableDevBoxAddOnSaga);
}

export function* enableDevBoxAddOnWithPollingAcceptedListenerSaga(): SagaIterator {
    yield takeEvery(enableDevBoxAddOnAccepted, enableDevBoxAddOnWithPollingAcceptedSaga);
}

export function* enableDevBoxAddOnWithPollingListenerSaga(): SagaIterator {
    yield takeEvery(enableDevBoxAddOnWithPolling, enableDevBoxAddOnWithPollingSaga);
}
